import React, {useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";

import {useAppKitNetwork} from "@reown/appkit/react";
import {ERC1155Abi} from "@modernize-games/nft-sdk";
import {useTranslation} from "react-i18next";
import {ethers} from "ethers";

import {
    PROCESS,
    deepClone,
    SORT_OPTIONS_V3,
    STANDARD_LIMIT_OPTIONS,
    formatNumberToEuropeanStyle,
    INVENTORY_FILTRATION_DATA_BAZAAR,
    ORDERS_REQUEST_DEFAULT_DATA_BAZAAR
} from "utils";
import {useGetBazzarInventoriesQuery} from "store/Global/global.api";
import {useAuthorization, useDebounce} from "hooks";
import {CHAIN_TO_PARAMS_MAP} from "web3/chains";

import {
    ItemCard,
    NoCosmetic,
    FiltersMobile,
    FiltersSideBar,
    PaginationWithTools,
    FilterSideBarSkeleton
} from "components/molecules";
import {GridContainer, Input, Refresh, SelectDropDownDisplay, SkeletonNftCart} from "components/atoms";
import {SortMobile} from "../Locker/LockerListedCosmetics";

const BazaarListedCosmetics = () => {
    const inventoryFiltrationDefaultData = deepClone(INVENTORY_FILTRATION_DATA_BAZAAR)
    const ordersRequestDefaultData = deepClone(ORDERS_REQUEST_DEFAULT_DATA_BAZAAR)

    const parentSectionRef = useRef(null)
    const {accessToken} = useAuthorization()
    const navigate = useNavigate()
    const {chainId} = useAppKitNetwork()
    const debounce = useDebounce()
    const {t} = useTranslation()

    const [filtration, setFiltration] = useState(inventoryFiltrationDefaultData)
    const [requestState, setRequestState] = useState(ordersRequestDefaultData)
    const [nameValue, setNameValue] = useState("")
    const [nftData, setNftData] = useState([])

    const {userData} = useSelector(state => state.authV2.signIn)

    const {data, isLoading, isFetching} = useGetBazzarInventoriesQuery({
        chain_id: chainId,
        ...requestState,
    }, {
        skip: accessToken ? !userData?.id : false,
        refetchOnMountOrArgChange: true
    })

    const navigateTo = (details) => {
        const {token_id} = details
        const chainParams = CHAIN_TO_PARAMS_MAP[chainId]
        const {addresses, network} = chainParams

        navigate(`/bazaar/inventory/${network}/${addresses.COLLECTION_ADDRESS}/${token_id}`)
    }

    const updateRequestState = (newState) => setRequestState({...requestState, ...newState});

    const setInitialStateOfPagination = () => {
        setFiltration(prev => {
            prev.page.value = 1
            prev.limit.value = STANDARD_LIMIT_OPTIONS[2]

            return prev
        })
    }

    const requestStateHandler = async (event) => {
        const {value, name} = event.target

        if (name === "searchByName") {
            setNameValue(value)

            const callBack = () => {
                updateRequestState({value: value, offset: 0, limit: 12})
                setInitialStateOfPagination()
            }

            debounce(callBack, 500)
        } else {
            window.scrollTo({top: parentSectionRef.current.offsetTop - 100, left: 0, behavior: 'smooth'})

            setFiltration(prev => {
                switch (name) {
                    case "orderBy":
                        prev.page.value = 1
                        prev.orderBy.value = value.value
                        updateRequestState({orderBy: value.value.value})
                        break
                    case "limit":
                        prev.page.value = 1
                        prev.limit.value = value.value
                        updateRequestState({offset: 0, limit: value.value.value})
                        break
                    case "page":
                        prev.page.value = value
                        updateRequestState({offset: requestState.limit * (value ? value - 1 : 0)})
                        break
                    case "refresh":
                        prev = inventoryFiltrationDefaultData
                        updateRequestState(ordersRequestDefaultData)
                        break
                    default:
                        return prev
                }

                return prev
            })
        }
    }

    const filtrationHandler = (attribute, sectionName) => {
        setFiltration(prev => {
            const prevCopy = {...prev}

            prevCopy.filter.find(section => section.name === sectionName).attributes.forEach(attr => {
                if (attr.id === attribute.id) attr.checked = !attr.checked
            })

            return prevCopy
        })
    }

    const applyFiltration = () => {
        const applyData = {};

        filtration.filter.forEach(({filedName, attributes}) => {
            applyData[filedName] = attributes
                .filter(({checked}) => checked)
                .map(({id}) => id);
        });

        updateRequestState({...applyData, limit: 36, offset: 0})
        setInitialStateOfPagination()
    };

    const checkNFTsAvailability = async (retryCount = 0, maxRetries = 3) => {
        if (!userData?.wallet || !data?.items) return

        try {
            const tokenIds = data.items.map(item => item.token_id)
            const chainParams = CHAIN_TO_PARAMS_MAP[chainId]
            const provider = new ethers.providers.JsonRpcProvider(chainParams.rpcURL)
            const erc1155Contract = new ethers.Contract(chainParams.addresses.COLLECTION_ADDRESS, ERC1155Abi, provider)

            const chainNFTsBalanceOf = await erc1155Contract.balanceOfBatch(Array.from({length: tokenIds.length}, () => userData.wallet), tokenIds)

            const nftsResults = tokenIds.map((tokenId, index) => ({
                token_id: tokenId,
                balance: Number(chainNFTsBalanceOf[index].toString()),
            }))

            setNftData(nftsResults)
        } catch (error) {
            if (retryCount < maxRetries) {
                checkNFTsAvailability(retryCount + 1, maxRetries);
            } else {
                console.error("Failed to fetch NFT availability:", error);
            }
        }
    }

    useEffect(() => {
        setFiltration(inventoryFiltrationDefaultData)
        updateRequestState({...ordersRequestDefaultData, chain_id: chainId})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chainId]);

    useEffect(() => {
        checkNFTsAvailability()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const isFirstRequest = data === undefined

    return (
        <div className="bazaar_cosmetics_content_general_listed" ref={parentSectionRef}>
            <div className="bazaar_cosmetics_content_general_listed_filter-container">
                {!isLoading && data !== undefined && <FiltersSideBar
                    details={filtration.filter}
                    applyCallBack={() => applyFiltration()}
                    setSideBarDetailsCallBack={(attribute, sectionName) => filtrationHandler(attribute, sectionName)}
                />}
                {isFirstRequest && <FilterSideBarSkeleton/>}
            </div>
            <div className="bazaar_cosmetics_content_general_listed_items-container">
                {isFirstRequest &&
                    <React.Fragment>
                        <div className="bazaar_cosmetics_content_general_listed_items-container_filtoration-tools">
                            <div className="refresh-skeleton skeleton"/>
                            <div className="input-skeleton skeleton"/>
                            <div className="select-skeleton skeleton"/>
                        </div>
                        <div
                            className="bazaar_cosmetics_content_general_listed_items-container_filtoration-tools_mobile"
                        >
                            <div className="filter-mobile-skeleton skeleton"/>
                            <div className="select-mobile-skeleton skeleton"/>
                        </div>
                    </React.Fragment>}
                {data?.items &&
                    <div className="bazaar_cosmetics_content_general_listed_items-container_filtoration-tools">
                        <Refresh refreshCallBack={() => requestStateHandler({target: {name: "refresh", value: ""}})}/>
                        <Input
                            skeleton={true}
                            icon="search"
                            value={nameValue}
                            name="searchByName"
                            placeholder={t("searchByNameOrAttribute")}
                            onChange={(e) => requestStateHandler(e)}
                        />
                        <SelectDropDownDisplay
                            name="orderBy"
                            btnText="Sorted by"
                            optionsList={SORT_OPTIONS_V3}
                            isPrepareOptionsForLang={true}
                            value={filtration.orderBy.value}
                            onChange={(e) => requestStateHandler(e)}
                        />
                        <div
                            className="bazaar_cosmetics_content_general_listed_items-container_filtoration-tools_mobile"
                        >
                            <FiltersMobile
                                details={filtration.filter}
                                applyCallBack={() => applyFiltration()}
                                setSideBarDetailsCallBack={(attribute, sectionName) => filtrationHandler(attribute, sectionName)}
                            />
                            <SortMobile
                                requestState={requestState}
                                requestStateHandler={(e) => requestStateHandler(e)}
                            />
                        </div>
                    </div>
                }
                {(isFirstRequest || isLoading || isFetching)
                    ? <GridContainer>
                        <SkeletonNftCart
                            type="listed"
                            cartLineCount={data?.items?.length ?? requestState.limit}
                        />
                    </GridContainer>
                    : !!data?.total
                        ? <GridContainer>
                            {data?.items.map(item =>
                                <ItemCard
                                    key={item.id}
                                    name={item.name}
                                    img={item.image_url}
                                    rarity={`card_${item.rarity}`}
                                    isRetired={!!item.never_sold_again}
                                    isActive={!!item.active_orders_count}
                                    onClick={() => navigateTo(item)}
                                    isOwned={nftData.find(nft => nft.token_id === item.token_id)?.balance > 0}
                                    price={item.lowest_price_kompete ? formatNumberToEuropeanStyle(item.lowest_price_kompete,true) : 0}
                                />
                            )}
                        </GridContainer>
                        : <NoCosmetic text={PROCESS.NO_BAZAAR_LISTED_COSMETICS}/>
                }
                {!isLoading && data !== undefined && <PaginationWithTools
                    totalNft={data?.total}
                    limitData={filtration.limit.data}
                    currentPage={filtration.page.value}
                    limitValue={filtration.limit.value}
                    callBackLimit={(e) => requestStateHandler(e)}
                    callBackCurrentPage={(e) => requestStateHandler({target: {name: "page", value: e}})}
                />}
            </div>
        </div>
    )
}

export default BazaarListedCosmetics